import React, { useMemo } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import BulkAssignShiftsFacilityPicker from './BulkAssignShiftsFacilityPicker';
import BulkAssignShiftsProfessionalInput from './BulkAssignShiftsProfessionalInput';
import BulkAssignShiftsSchedulePane from './BulkAssignShiftsSchedulePane';
import ShiftsForm from '../ShiftsForm';
import facilityInfoIcon from '../../../../assets/images/facility-info-icon.svg';

import './BulkAssignShiftsTab.scss';

const SHIFTS_SECTION_INFO_TEXT = 'Add up to 30 dates total for the selected professional.';

const FacilityLabel = () => (
	<div className="facility-label">
		Facility
		<OverlayTrigger
			placement="right"
			delay={{ show: 100, hide: 400 }}
			overlay={
				<Tooltip className="info-facility-tooltip">
					This list will only include facilities where you have permission to assign flex shifts.
				</Tooltip>
			}
		>
			<img src={facilityInfoIcon} alt="info" />
		</OverlayTrigger>
	</div>
);

const BulkAssignShiftsTab = ({
	facilities,
	permissionsByFacility,
	isGlobalCalendar,
	selectedFacility,
	onFacilityChange,
	currentShiftStructures,
	shiftsGroups,
	updateShift,
	removeShift,
	addEmptyShiftGroup,
	errors,
	showUnits,
	currentFacilityUnits,
	maxShiftsToCreate,
	checkIfCanAddTodayShiftForStructure,
	isMobile,
	selectedCaregiver,
	onCaregiverChange,
	overlapErrors,
	getDateConflict
}) => {
	const selectedCaregiverId = selectedCaregiver?.id;

	// Once a caregiver is selected, restrict shift structures to the licenses and specialties
	// the caregiver actually holds, so the Shift type dropdown only offers valid choices.
	const structuresForCaregiver = useMemo(() => {
		if (!selectedCaregiver) return currentShiftStructures;
		const licenseCodes = new Set(selectedCaregiver.licenseCodes || []);
		const specialtyCodes = new Set(selectedCaregiver.specialtyCodes || []);
		return (currentShiftStructures || []).filter((structure) => {
			if (!licenseCodes.has(structure.license_code)) return false;
			if (structure.specialty_code && !specialtyCodes.has(structure.specialty_code)) return false;
			return true;
		});
	}, [currentShiftStructures, selectedCaregiver]);

	const errorsWithOverlaps = useMemo(() => {
		const merged = { ...errors };
		Object.entries(merged || {}).forEach(([groupId, groupErrors]) => {
			merged[groupId] = { ...groupErrors, bulkAssignError: true };
		});
		Object.entries(overlapErrors || {}).forEach(([groupId, groupErrors]) => {
			merged[groupId] = { ...(merged[groupId] || {}), ...groupErrors, bulkAssignError: true };
		});
		return merged;
	}, [errors, overlapErrors]);

	return (
		<div className="bulk-assign-shifts-tab">
			<div className="layout">
				<div className="left">
					<div className="schedule-container">
						<BulkAssignShiftsSchedulePane facilityId={selectedFacility?.id} caregiverId={selectedCaregiverId} />
					</div>
				</div>
				<div className="right">
					<div className="section-header">{isGlobalCalendar ? 'Facility and professional' : 'Professional'}</div>
					<div className="fields-row">
						{isGlobalCalendar && (
							<div className="field">
								<FacilityLabel />
								<BulkAssignShiftsFacilityPicker
									facilities={facilities}
									permissionsByFacility={permissionsByFacility}
									selectedFacility={selectedFacility}
									onFacilityChange={onFacilityChange}
								/>
							</div>
						)}
						<div className="field">
							<div className="select-label">Professional</div>
							<BulkAssignShiftsProfessionalInput
								facilityId={selectedFacility?.id}
								selectedCaregiver={selectedCaregiver}
								onCaregiverChange={onCaregiverChange}
								disabled={!selectedFacility}
							/>
						</div>
					</div>
					<ShiftsForm
						infoText={SHIFTS_SECTION_INFO_TEXT}
						shiftsGroups={shiftsGroups}
						updateShift={updateShift}
						removeShift={removeShift}
						addEmptyShiftGroup={addEmptyShiftGroup}
						errors={errorsWithOverlaps}
						currentShiftStructures={structuresForCaregiver}
						isFlex
						isHybrid={false}
						isGlobalCalendar={isGlobalCalendar}
						selectedFacility={selectedCaregiver ? selectedFacility : null}
						showUnits={showUnits}
						currentFacilityUnits={currentFacilityUnits}
						maxShiftsToCreate={maxShiftsToCreate}
						checkIfCanAddTodayShiftForStructure={checkIfCanAddTodayShiftForStructure}
						isMobile={isMobile}
						hideProvidersCount
						getDateConflict={getDateConflict}
					/>
				</div>
			</div>
		</div>
	);
};

export default BulkAssignShiftsTab;
